// Deliberate exception to the "all HTTP goes through api.ts" rule: this module
// reports API failures, so routing it through api.ts would recurse on failure.
// It needs no auth (public endpoint), must never throw, and swallows its own
// errors instead of reporting them.

export type ReporterUserType = 'anonymous' | 'customer' | 'company' | 'staff' | 'technician';

export interface ClientLogEntry {
  level: 'error' | 'warn';
  message: string;
  stack?: string;
  url?: string;
  requestId?: string;
  appointmentRef?: string;
}

const API_BASE = import.meta.env.VITE_API_URL
  ? import.meta.env.VITE_API_URL.replace(/\/$/, '')
  : '';

const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 10;

const SAFE_REQUEST_ID = /^[A-Za-z0-9-]{8,64}$/;

const sanitizeRequestId = (value: unknown): string | undefined =>
  typeof value === 'string' && SAFE_REQUEST_ID.test(value) ? value : undefined;

let queue: Array<ClientLogEntry & { userType: ReporterUserType; occurredAt: string }> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lastEntryKey = '';
let currentUserType: ReporterUserType = 'anonymous';

export const setErrorReporterUserType = (userType: ReporterUserType) => {
  currentUserType = userType;
};

const flush = () => {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const entries = queue.slice(0, MAX_BATCH);
  queue = [];
  lastEntryKey = '';

  try {
    fetch(`${API_BASE}/api/client-logs`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries })
    }).catch(() => {
      /* never report reporter failures */
    });
  } catch {
    /* fetch itself threw (no network stack in some test envs) — swallow */
  }
};

export const reportClientError = (entry: ClientLogEntry) => {
  const key = `${entry.level}|${entry.message}|${entry.stack || ''}`;
  if (key === lastEntryKey) return;
  lastEntryKey = key;

  queue.push({
    ...entry,
    message: entry.message.slice(0, 2000),
    stack: entry.stack ? entry.stack.slice(0, 8192) : undefined,
    url: entry.url || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    userType: currentUserType,
    occurredAt: new Date().toISOString()
  });

  if (queue.length >= MAX_BATCH) {
    flush();
    return;
  }
  if (flushTimer === null) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
};

export const installGlobalErrorReporting = () => {
  window.addEventListener('error', (event) => {
    reportClientError({
      level: 'error',
      message: event.message || 'Unhandled window error',
      stack: event.error?.stack
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportClientError({
      level: 'error',
      message:
        reason instanceof Error ? `Unhandled rejection: ${reason.message}` : 'Unhandled rejection',
      stack: reason instanceof Error ? reason.stack : undefined,
      requestId:
        typeof reason === 'object' && reason !== null
          ? sanitizeRequestId((reason as { requestId?: unknown }).requestId)
          : undefined
    });
  });
};

export const __resetErrorReporterForTests = () => {
  queue = [];
  lastEntryKey = '';
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  currentUserType = 'anonymous';
};
